import { useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SEARCH_FORM, MOON } from "@/content/moon";
import { useLang } from "@/lib/lang";
import { formatApiErrorDetail } from "@/lib/api";

const TYPES = ["achat", "location", "piece_rare"];
const URGENCIES = ["normale", "elevee", "urgente"];
const MAX_FILE = 5 * 1024 * 1024;

export default function MoonSearch() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const cat = params.get("categorie");
  const productParam = params.get("produit");
  const typeParam = params.get("type");
  const catObj = cat ? MOON.categories.find((c) => c.slug === cat) : null;
  const prefill = catObj
    ? lang === "en" ? `Category: ${catObj.label.en}. ` : `Catégorie : ${catObj.label.fr}. `
    : "";

  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "", province: "", city: "",
    product: (productParam ? `${productParam}. ` : "") + prefill,
    manufacturer: "", quantity: "", request_type: typeParam && TYPES.includes(typeParam) ? typeParam : cat ? "piece_rare" : "achat",
    urgency: "normale", desired_date: "", budget: "", specs: "", comments: "", consent: false,
    company_website: "",
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [requestNumber, setRequestNumber] = useState("");
  const [emailSent, setEmailSent] = useState(true);
  const [serverError, setServerError] = useState("");
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    setFileError("");
    if (!f) return;
    if (f.size > MAX_FILE) {
      setFileError(t(SEARCH_FORM.fileTooBig));
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFile(f);
  };

  const validate = () => {
    const errs = {};
    if (!form.company.trim()) errs.company = t(SEARCH_FORM.required);
    if (!form.contact_name.trim()) errs.contact_name = t(SEARCH_FORM.required);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t(SEARCH_FORM.invalidEmail);
    if (!form.province.trim()) errs.province = t(SEARCH_FORM.required);
    if (!form.city.trim()) errs.city = t(SEARCH_FORM.required);
    if (!form.product.trim()) errs.product = t(SEARCH_FORM.required);
    if (!form.consent) errs.consent = t(SEARCH_FORM.required);
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("sending");
    setServerError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "consent") fd.append(k, v ? "true" : "false");
        else if (v !== "" && v != null) fd.append(k, v);
      });
      fd.append("lang", lang);
      if (file) fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/equipment-requests`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(formatApiErrorDetail(data.detail || data.error));
      setRequestNumber(data.request_number);
      setEmailSent(data.email_sent !== false);
      setStatus("success");
    } catch (err) {
      setServerError(formatApiErrorDetail(err.message));
      setStatus("error");
    }
  };

  const inputCls = (k) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-sm text-navy-950 outline-none transition-colors placeholder:text-steel/70 focus:border-electric ${errors[k] ? "border-red-400" : "border-slate-200"}`;
  const err = (k) => errors[k] && <p role="alert" className="mt-1 text-xs text-red-500">{errors[k]}</p>;

  if (status === "success") {
    return (
      <main className="bg-paper pb-24 pt-[152px] md:pt-[200px]" data-testid="moon-search-success-page">
        <div className="mx-auto max-w-2xl px-5 md:px-8">
          <div className="rounded-2xl border border-slate-200/90 bg-white p-10 text-center shadow-card">
            <span className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-badge text-electric"><CheckCircle2 size={32} /></span>
            <h1 className="mt-6 font-display text-2xl font-bold text-navy-950 md:text-3xl" data-testid="moon-success-title">{t(SEARCH_FORM.success)}</h1>
            <p className="mt-5 inline-block rounded-full bg-ice px-6 py-3 font-mono text-lg font-bold tracking-wider text-sky-700" data-testid="moon-request-number">{requestNumber}</p>
            <p className="mt-5 text-sm leading-relaxed text-graphite">{t(SEARCH_FORM.successSub)}</p>
            {!emailSent && <p className="mt-3 text-xs text-steel">Notification courriel : en attente de configuration du service de messagerie.</p>}
            <button onClick={() => { setStatus("idle"); setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-bold text-navy-800 transition-colors hover:border-electric hover:text-electric" data-testid="moon-success-reset">
              {t(SEARCH_FORM.again)}
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="moon-search-page">
      <Seo
        title={lang === "fr"
          ? "Trouver un équipement rare — MOON Supply | Export Monde Prestige Inc."
          : "Find a rare equipment — MOON Supply | Export Monde Prestige Inc."}
        description={t(SEARCH_FORM.intro)}
        path="/moon/recherche"
      />
      <section className="bg-gradient-to-b from-ice via-white to-white pb-12 pt-[152px] md:pt-[200px]">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">MOON Supply</p>
            <h1 className="mt-4 font-display text-3xl font-bold leading-[1.1] tracking-tight text-navy-950 md:text-5xl">{t(SEARCH_FORM.title)}</h1>
            <p className="mt-5 text-base leading-relaxed text-graphite">{t(SEARCH_FORM.intro)}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Reveal>
            <form onSubmit={onSubmit} noValidate className="rounded-2xl border border-slate-200/90 bg-white p-7 shadow-card md:p-10" data-testid="moon-search-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="m-company" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.company)} *</label>
                  <input id="m-company" data-testid="moon-company-input" className={inputCls("company")} value={form.company} onChange={set("company")} />
                  {err("company")}
                </div>
                <div>
                  <label htmlFor="m-contact" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.contactName)} *</label>
                  <input id="m-contact" data-testid="moon-contact-input" className={inputCls("contact_name")} value={form.contact_name} onChange={set("contact_name")} />
                  {err("contact_name")}
                </div>
                <div>
                  <label htmlFor="m-email" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.email)} *</label>
                  <input id="m-email" type="email" data-testid="moon-email-input" className={inputCls("email")} value={form.email} onChange={set("email")} />
                  {err("email")}
                </div>
                <div>
                  <label htmlFor="m-phone" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.phone)}</label>
                  <input id="m-phone" type="tel" data-testid="moon-phone-input" className={inputCls("phone")} value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label htmlFor="m-province" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.province)} *</label>
                  <input id="m-province" data-testid="moon-province-input" placeholder={lang === "fr" ? "ex. : Québec" : "e.g. Quebec"} className={inputCls("province")} value={form.province} onChange={set("province")} />
                  {err("province")}
                </div>
                <div>
                  <label htmlFor="m-city" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.city)} *</label>
                  <input id="m-city" data-testid="moon-city-input" placeholder={lang === "fr" ? "ex. : Laurier-Station" : "e.g. Laurier-Station"} className={inputCls("city")} value={form.city} onChange={set("city")} />
                  {err("city")}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="m-product" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.product)} *</label>
                <input id="m-product" data-testid="moon-product-input" placeholder={t(SEARCH_FORM.productPlaceholder)} className={inputCls("product")} value={form.product} onChange={set("product")} />
                {err("product")}
              </div>
              <div className="mt-5">
                <label htmlFor="m-manufacturer" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.manufacturer)}</label>
                <input id="m-manufacturer" data-testid="moon-manufacturer-input" className={inputCls("manufacturer")} value={form.manufacturer} onChange={set("manufacturer")} />
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-3">
                <div>
                  <label htmlFor="m-quantity" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.quantity)}</label>
                  <input id="m-quantity" type="number" min="1" data-testid="moon-quantity-input" className={inputCls("quantity")} value={form.quantity} onChange={set("quantity")} />
                </div>
                <div>
                  <label htmlFor="m-type" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.type)} *</label>
                  <select id="m-type" data-testid="moon-type-select" className={inputCls("request_type")} value={form.request_type} onChange={set("request_type")}>
                    {TYPES.map((tp) => <option key={tp} value={tp}>{t(MOON.types[tp])}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="m-urgency" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.urgency)}</label>
                  <select id="m-urgency" data-testid="moon-urgency-select" className={inputCls("urgency")} value={form.urgency} onChange={set("urgency")}>
                    {URGENCIES.map((u) => <option key={u} value={u}>{t(MOON.urgencies[u])}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="m-date" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.desiredDate)}</label>
                  <input id="m-date" data-testid="moon-date-input" placeholder={lang === "fr" ? "ex. : avril 2026" : "e.g. April 2026"} className={inputCls("desired_date")} value={form.desired_date} onChange={set("desired_date")} />
                </div>
                <div>
                  <label htmlFor="m-budget" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.budget)}</label>
                  <input id="m-budget" data-testid="moon-budget-input" className={inputCls("budget")} value={form.budget} onChange={set("budget")} />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="m-specs" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.specs)}</label>
                <textarea id="m-specs" rows={4} data-testid="moon-specs-textarea" placeholder={t(SEARCH_FORM.specsHint)} className={inputCls("specs")} value={form.specs} onChange={set("specs")} />
              </div>

              <div className="mt-5">
                <label htmlFor="m-file" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.file)}</label>
                <input id="m-file" ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="moon-file-input" onChange={onFile}
                  className="w-full cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-graphite file:mr-3 file:rounded-full file:border-0 file:bg-badge file:px-4 file:py-2 file:text-xs file:font-bold file:text-sky-700" />
                {fileError && <p role="alert" className="mt-1 text-xs text-red-500">{fileError}</p>}
              </div>

              <div className="mt-5">
                <label htmlFor="m-comments" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SEARCH_FORM.comments)}</label>
                <textarea id="m-comments" rows={3} data-testid="moon-comments-textarea" className={inputCls("comments")} value={form.comments} onChange={set("comments")} />
              </div>

              {/* Honeypot antispam */}
              <div className="absolute left-[-9999px]" aria-hidden="true">
                <label htmlFor="m-website">Ne pas remplir</label>
                <input id="m-website" type="text" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set("company_website")} />
              </div>

              <div className="mt-6">
                <label className="flex cursor-pointer items-start gap-3" data-testid="moon-consent-checkbox">
                  <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 rounded border-slate-300 accent-electric" aria-invalid={!!errors.consent} />
                  <span className="text-[13px] leading-relaxed text-graphite">{t(SEARCH_FORM.consent)}</span>
                </label>
                {err("consent")}
              </div>

              {status === "error" && (
                <div role="alert" className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="moon-form-error">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{t(SEARCH_FORM.error)} {serverError && `(${serverError})`}</span>
                </div>
              )}

              <button type="submit" disabled={status === "sending"} data-testid="moon-form-submit"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-electric px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-electric-dark disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                {status === "sending" ? t(SEARCH_FORM.sending) : t(SEARCH_FORM.submit)}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
